import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  getFirestore,
  serverTimestamp,
  where,
  deleteDoc
} from 'firebase/firestore';
import { Tarea, Obra } from '../types';
import { useAuth } from './AuthContext';
import { useObras } from './ObrasContext';

const db = getFirestore();

interface TareasContextType {
  tareas: Tarea[];
  loading: boolean;
  error: string | null;
  agregarTarea: (nombre: string, fecha: Date, obraId: string, recurrente?: boolean) => Promise<void>;
  completarTarea: (id: string) => Promise<void>;
  actualizarTarea: (id: string, data: Partial<Tarea>) => Promise<void>;
  eliminarTarea: (id: string) => Promise<void>;
}

const TareasContext = createContext<TareasContextType | undefined>(undefined);

export const useTareas = () => {
  const context = useContext(TareasContext);
  if (!context) {
    throw new Error('useTareas debe ser usado dentro de un TareasProvider');
  }
  return context;
};

export const TareasProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tareas, setTareas] = useState<Tarea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { usuario } = useAuth();
  const { obraSeleccionada, obras } = useObras();

  // Encuentra la obra seleccionada del array de obras
  const obraActual = obras.find(obra => obra.id === obraSeleccionada);

  useEffect(() => {
    if (!usuario) {
      setTareas([]);
      setLoading(false);
      return;
    }

    const tareasRef = collection(db, 'tareas');
    const q = query(tareasRef); // Fetch properly filtered by user permissions inside the snapshot if needed, or rely on client side filtering below

    const unsubscribe = onSnapshot(q,
      (querySnapshot) => {
        const tareasData: Tarea[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          tareasData.push({
            id: doc.id,
            nombre: data.nombre,
            completada: data.completada,
            fechaCreacion: data.fechaCreacion ? (data.fechaCreacion.toDate ? data.fechaCreacion.toDate() : new Date(data.fechaCreacion)) : new Date(),
            fechaCompletacion: data.fechaCompletacion ? (data.fechaCompletacion.toDate ? data.fechaCompletacion.toDate() : new Date(data.fechaCompletacion)) : undefined,
            obraId: data.obraId,
            obraNombre: data.obraNombre,
            prioridad: data.prioridad,
            fechaProgramada: data.fechaProgramada ? (data.fechaProgramada.toDate ? data.fechaProgramada.toDate() : new Date(data.fechaProgramada)) : undefined,
            recurrente: data.recurrente || false,
            completionesRecurrentes: data.completionesRecurrentes || [],
            esUrgente: data.esUrgente || false,
            order: data.order
          });
        });

        // Client-side filtering: Show all tasks the user has access to
        // If admin or canSeeAll, show everything.
        // Otherwise, only show tasks where obraId is in the user's assigned works.
        let filtered = tareasData;
        if (usuario.role !== 'ADMIN' && !usuario.puedeVerTodasLasObras) {
          filtered = filtered.filter(t => usuario.obrasAsignadas?.includes(t.obraId));
        }

        setTareas(filtered);
        setLoading(false);
      },
      (error) => {
        console.error(error);
        setError('Error al cargar las tareas');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [usuario]); // Removed obraSeleccionada dependency

  const agregarTarea = async (nombre: string, fecha: Date, obraId: string, recurrente: boolean = false) => {
    if (!usuario) return;

    const targetObra = obras.find(o => o.id === obraId);
    if (!targetObra) {
      throw new Error("Obra no encontrada o no tienes acceso");
    }

    try {
      await addDoc(collection(db, 'tareas'), {
        nombre,
        completada: false,
        fechaCreacion: serverTimestamp(), // Firestore timestamp
        // usuarioId: usuario.uid, // Not in schema but good practice
        // usuarioEmail: usuario.email, // Not in schema
        obraId: targetObra.id,
        obraNombre: targetObra.nombre,
        fechaProgramada: fecha || serverTimestamp(), // Use provided date or default
        recurrente: recurrente || false,
        completionesRecurrentes: []
      });
    } catch (err: any) {
      setError(err.message || 'Error al agregar la tarea');
      throw err;
    }
  };

  const completarTarea = async (id: string) => {
    if (!usuario) return;

    try {
      const tareaRef = doc(db, 'tareas', id);
      await updateDoc(tareaRef, {
        completada: true,
        fechaCompletacion: serverTimestamp()
      });
    } catch (err: any) {
      setError(err.message || 'Error al completar la tarea');
      throw err;
    }
  };

  const actualizarTarea = async (id: string, data: Partial<Tarea>) => {
    if (!usuario) return;

    try {
      const tareaRef = doc(db, 'tareas', id);
      await updateDoc(tareaRef, data);
    } catch (err: any) {
      setError(err.message || 'Error al actualizar la tarea');
      throw err;
    }
  };

  const eliminarTarea = async (id: string) => {
    if (!usuario) return;

    try {
      await deleteDoc(doc(db, 'tareas', id));
    } catch (err: any) {
      setError(err.message || 'Error al eliminar la tarea');
      throw err;
    }
  };

  const value = {
    tareas,
    loading,
    error,
    agregarTarea,
    completarTarea,
    actualizarTarea,
    eliminarTarea
  };

  return (
    <TareasContext.Provider value={value}>
      {children}
    </TareasContext.Provider>
  );
};